import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { useTranslation } from 'react-i18next'
import { SizingModelResult } from '@/domain/sizing'
import { formatPercent } from '@/utils/format'

export interface SizingRates {
  beforePct: number
  afterPct: number
}

/** Total sizing return rate over the models with a notice, before vs after.
 * "Before" is each model's counterfactual rate applied to the units it sold
 * under the notice — the same subtraction the euros come from, so the chart
 * reconciles with the total and does not move with the mix of models. Retired
 * models keep their frozen measurement and count too. Null when nothing is
 * measured. */
export function sizingRates(results: SizingModelResult[]): SizingRates | null {
  const measured = results
    .map((row) => ({ row, before: beforeRate(row) }))
    .filter(
      ({ row, before }) =>
        row.classification !== 'INELIGIBLE' &&
        before !== null &&
        (row.unitsAfter ?? 0) > 0
    )
  if (measured.length === 0) {
    return null
  }
  // Unit-weighted: averaging per-model rates would give a 5-unit model the same
  // weight as a 5,000-unit one.
  const totals = measured.reduce(
    (acc, { row, before }) => {
      const units = row.unitsAfter ?? 0
      acc.units += units
      acc.expectedBefore += (before ?? 0) * units
      acc.returnedAfter +=
        (row.retTargetAfter ?? 0) + (row.retOppositeAfter ?? 0)
      return acc
    },
    { units: 0, expectedBefore: 0, returnedAfter: 0 }
  )
  return {
    beforePct: (totals.expectedBefore / totals.units) * 100,
    afterPct: (totals.returnedAfter / totals.units) * 100
  }
}

/** The counterfactual the row was billed against; rows persisted before it was
 * carried fall back to their raw before-window counts. */
function beforeRate(row: SizingModelResult): number | null {
  if (row.rateBeforeTotal !== null) {
    return row.rateBeforeTotal
  }
  if (
    (row.unitsBefore ?? 0) > 0 &&
    row.retTargetBefore !== null &&
    row.retOppositeBefore !== null
  ) {
    return (
      (row.retTargetBefore + row.retOppositeBefore) / (row.unitsBefore ?? 1)
    )
  }
  return null
}

export function SizingRatesChart({
  results
}: {
  results: SizingModelResult[]
}) {
  const { t, i18n } = useTranslation()
  const rates = sizingRates(results)

  if (!rates) {
    return (
      <p className="py-4 text-sm text-neutral-500">
        {t('sizing.overview.reasons_empty')}
      </p>
    )
  }

  const pct = (value: number) => formatPercent(value, i18n.language)

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'Roobert, sans-serif'
    },
    plotOptions: {
      bar: {
        horizontal: true,
        distributed: true,
        borderRadius: 3,
        barHeight: '56%',
        dataLabels: { position: 'top' }
      }
    },
    colors: ['#d4d4d4', '#1790ff'],
    dataLabels: {
      enabled: true,
      textAnchor: 'start',
      offsetX: 6,
      formatter: (value) => pct(Number(value)),
      style: { fontSize: '10px', fontWeight: 500, colors: ['#525252'] }
    },
    grid: { borderColor: '#e5e5e5', strokeDashArray: 3 },
    legend: { show: false },
    xaxis: {
      categories: [
        t('sizing.overview.reasons_before'),
        t('sizing.overview.reasons_after')
      ],
      labels: {
        formatter: (value) => `${Number(value).toFixed(0)}%`,
        style: { colors: '#737373', fontSize: '10px' }
      },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: { style: { colors: '#737373', fontSize: '11px' } }
    },
    tooltip: { y: { formatter: (value) => pct(value) } }
  }

  const round = (value: number) => Number(value.toFixed(2))

  return (
    <ReactApexChart
      type="bar"
      height={220}
      options={options}
      series={[
        {
          name: t('sizing.overview.reasons_title'),
          data: [round(rates.beforePct), round(rates.afterPct)]
        }
      ]}
    />
  )
}
